import { z } from 'zod';
import {
  APP_SYNC_MAX_EVENTS,
  CLIENT_EVENT_ID_MAX_LENGTH,
  DUTY_EVENT_ATTESTATIONS,
  DUTY_EVENT_TYPES,
  GEO_ACCURACY_MAX_METRES,
  GEO_STATUSES,
  ATTENDANCE_STATES,
} from '../constants/dutyEvent.constant';
import type { PresenceTally } from '../constants/presence.constant';
import { objectIdSchema } from './common.schema';

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  THE DUTY EVENT — WHAT HAPPENED ON THE GROUND (PA_REQUIREMENTS §5.4). [P5c]
 *
 *  ⚠️ THE SUBJECT OF AN EVENT IS `(deploymentId, subjectOfficerId)`. AN ASSIGNMENT ID IS
 *  AN ALIAS FOR THAT PAIR, NEVER THE IDENTITY.
 *
 *  The obvious shape keys everything on the assignment, and it cannot express two of the
 *  three things this pass is for. A COMMAND officer holds no assignment at all —
 *  `assignment.model.ts` says "NOTHING HERE KNOWS COMMAND EXISTS", and
 *  `commandRoleIsolation.test.ts` CLAIM 5 keeps it that way — so an Incharge marking their
 *  own duty has no id to send. And the detail payload gives `assignmentId` only on the
 *  CALLER's own row, so a supervisor marking somebody else has no id to send either.
 *
 *  So the pair is the identity, `assignmentId` is nullable on the record, and the four
 *  assignment-keyed routes are ADAPTERS that resolve `:id` to the pair. They carry no
 *  subject parameter, which is what makes it impossible for a path and a body to disagree
 *  about who is being marked.
 *
 *  ⚠️ `capturedAt` IS THE DEVICE CLOCK AND MAY BE WRONG. `syncedAt` IS THE SERVER'S.
 *  A phone that lost its battery boots at the epoch, and a marking taken in a basement may
 *  arrive hours later. Both are stored, always, and neither is ever reconciled against the
 *  other: `capturedAt` is the operationally meaningful time — when the officer was at the
 *  gate — and `syncedAt` is when we learned of it. An implausible gap is FLAGGED in the
 *  record rather than corrected, because correcting it would destroy the only evidence
 *  that the device's clock is wrong.
 *
 *  ⚠️ AND `clientEventId` IS THE WHOLE OF IDEMPOTENT REPLAY. It is minted on the device at
 *  CAPTURE — not at send — so the offline queue, a duplicated request and a double tap all
 *  resolve to one row. A repeat is a NO-OP that returns the original event, never a second
 *  row and never an error.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */

/**
 * Where the officer was, or why we do not know.
 *
 * ⚠️ THE REFINE IS AN EQUIVALENCE, IN BOTH DIRECTIONS, AND IT IS WHAT MAKES §4c SAFE.
 * `CAPTURED` requires coordinates; anything else forbids them. So a client that reports a
 * denial WITHOUT coordinates is CORRECT rather than merely tolerated — the shape of the
 * payload states which of the two happened, and no reader has to infer it from a null.
 *
 * ⚠️ IT IS A PLAIN BOOLEAN EXPRESSION AND CALLS NOTHING. A throw inside a zod `refine`
 * escapes `safeParse` and surfaces as an opaque 500 (measured on zod 4.4.3, CLAUDE.md §6),
 * so anything a refine touches must be total. Comparing two nullables is.
 */
export const dutyEventGeoSchema = z
  .object({
    status: z.enum(GEO_STATUSES),
    lat: z.number().min(-90).max(90).nullable(),
    lng: z.number().min(-180).max(180).nullable(),
    /** Metres, as the device reported it. `null` when the device did not say. */
    accuracyMetres: z.number().min(0).max(GEO_ACCURACY_MAX_METRES).nullable(),
  })
  .refine((geo) => (geo.status === 'CAPTURED') === (geo.lat !== null && geo.lng !== null), {
    message: 'Coordinates are required when a fix was captured, and forbidden when it was not',
  });

/**
 * ⚠️ A PLAIN `z.object`, SO THE ASSIGNMENT-KEYED BODY CAN BE DERIVED BY `.omit()`.
 *
 * A refined schema is not reliably `.omit()`-able, and hand-writing the second literal is
 * exactly the duplicated-contract drift divergence #1 exists to kill. So the base carries
 * no refinements and each exported schema re-applies them. Same discipline, same reason, as
 * "a `.partial()`-ed base schema carries no `.default()`" (§6).
 */
const dutyEventInputBaseSchema = z.object({
  /**
   * ⚠️ MINTED ON THE DEVICE AT CAPTURE, NOT AT SEND. That is what makes a marking taken in
   * a basement and sent an hour later the SAME event as the one the officer tapped — and it
   * is why a replay is a no-op rather than a duplicate.
   *
   * ⚠️ [C1c §2b] AND THE CHARSET IS NOW LOAD-BEARING FOR A SECOND REASON. One tap records
   * two rows, and the server mints the derived row's id as `~d<tapped>`. `~` is outside this
   * regex, so no client can mint into the derived namespace and no derived row can collide
   * with a tapped one — see `DERIVED_CLIENT_EVENT_ID_PREFIX`. Widening this regex, or raising
   * `CLIENT_EVENT_ID_MAX_LENGTH` past the column's bound, breaks that silently.
   */
  clientEventId: z
    .string()
    .trim()
    .min(8, 'A client event id is too short to be unique')
    .max(CLIENT_EVENT_ID_MAX_LENGTH)
    .regex(/^[A-Za-z0-9_-]+$/, 'A client event id may hold letters, digits, hyphen and underscore'),
  type: z.enum(DUTY_EVENT_TYPES),
  deploymentId: objectIdSchema,
  /** Absent means the caller themselves. Present means a commander is marking for someone. */
  subjectOfficerId: objectIdSchema.optional(),
  /**
   * Which stop on the route (FR-5.4.4). REQUIRED when the duty carries more than one, and
   * resolved to the only one when it does not — the server decides, because only it knows
   * how many stops the duty was stamped with.
   */
  locationId: z.string().trim().min(1).max(24).optional(),
  /** ⚠️ THE DEVICE CLOCK. The server records its own alongside and reconciles neither. */
  capturedAt: z.iso.datetime(),
  geo: dutyEventGeoSchema,
  /** ABSENT only, and required there — a commander stating a fact must say what it is. */
  reason: z.string().trim().min(1).max(300).optional(),
});

/**
 * ⚠️ `reason` BELONGS TO `ABSENT` AND TO NOTHING ELSE, IN BOTH DIRECTIONS.
 *
 * Required on an absence, because §6b's whole point is that a commander marking somebody
 * absent states why; refused elsewhere, so it cannot become a free-text field on a Start
 * Duty that nobody reads and nothing renders.
 */
export const reasonBelongsToAbsent = (input: { type: string; reason?: string | undefined }): boolean =>
  input.type === 'ABSENT' ? input.reason !== undefined : input.reason === undefined;

/** ⚠️ [C5c §5c] One sentence for the rule, so the schema and the writer's re-check cannot drift. */
export const REASON_BELONGS_TO_ABSENT_MESSAGE =
  'A reason is required when marking somebody absent, and is not accepted otherwise';

export const dutyEventInputSchema = dutyEventInputBaseSchema.refine(reasonBelongsToAbsent, {
  path: ['reason'],
  message: REASON_BELONGS_TO_ABSENT_MESSAGE,
});

/**
 * The body of the three PRESENCE assignment-keyed routes — `/start-duty`, `/reached`, `/stand-down`.
 *
 * ⚠️ IT CARRIES NO `type`, NO `deploymentId` AND NO `subjectOfficerId` — the route names the
 * type, and the assignment names the pair. A path and a body cannot disagree about who is
 * being marked if the body cannot say.
 *
 * ⚠️ [C5c §5c] AND NO `reason`, WHICH IT USED TO ACCEPT. The adapter body dropped
 * `reasonBelongsToAbsent` because a refined schema is not `.omit()`-able, so all four routes took
 * a reason optionally: `/absent` without one and a Start Duty with one. A reason on a presence
 * marking is now STRIPPED at the boundary — CLAUDE.md §6's rule for an unknown key, so an `app/`
 * build that sends one keeps working — and `/absent` has its own body below that requires it.
 */
export const assignmentDutyEventBodySchema = dutyEventInputBaseSchema.omit({
  type: true,
  deploymentId: true,
  subjectOfficerId: true,
  reason: true,
});

/**
 * ⚠️ [C5c §5c] `/absent`'s OWN BODY — THE REASON IS REQUIRED HERE, AS IT ALWAYS WAS ON THE
 * CANONICAL ROUTE. A commander stating a fact says what it is; `ABSENCE_REASONS` is the set Duty
 * Mitra sends, and the column stays free text for the reason that constant's header gives.
 */
export const assignmentAbsentBodySchema = assignmentDutyEventBodySchema.extend({
  reason: z.string().trim().min(1).max(300),
});

export const syncDutyEventsBodySchema = z.object({
  events: z.array(dutyEventInputSchema).min(1, 'A sync carries at least one event').max(APP_SYNC_MAX_EVENTS),
});

/**
 * What a write returns.
 *
 * ⚠️ NO COORDINATES, EVER, AND `distanceMetres` NEVER LEAVES THE SERVER EITHER.
 * FR-5.7.10: officer coordinates are location data on named government employees, access
 * controlled and excluded from routine exports. Echoing them back would put them in every
 * proxy log for no reader — the client already knows where it was.
 */
export const dutyEventSchema = z.object({
  id: objectIdSchema,
  clientEventId: z.string(),
  type: z.enum(DUTY_EVENT_TYPES),
  deploymentId: objectIdSchema,
  /** ⚠️ `null` when the subject is a command officer, who holds no force slot. */
  assignmentId: objectIdSchema.nullable(),
  subjectOfficerId: objectIdSchema,
  actorOfficerId: objectIdSchema,
  locationId: z.string().nullable(),
  capturedAt: z.iso.datetime(),
  syncedAt: z.iso.datetime(),
  /** The device and the server disagree by more than the tolerated skew. Recorded, not fixed. */
  clockImplausible: z.boolean(),
  reason: z.string().nullable(),
  /** The subject's state AFTER this event, so a client need not re-derive it. */
  attendance: z.enum(ATTENDANCE_STATES),
  /** ⚠️ `true` when this replayed an event already recorded. Not an error — the contract. */
  duplicate: z.boolean(),
  /**
   * ⚠️ [C1c §2d] WHO SAYS SO. `SELF` on a marking the subject tapped; `ON_BEHALF` when a
   * commander tapped it for them; `INFERRED` when the server derived it from another
   * marking. Derived on read from `actorOfficerId` versus `subjectOfficerId` and the row's
   * provenance — there is no stored `geoSource` and none may be added.
   */
  attestation: z.enum(DUTY_EVENT_ATTESTATIONS),
  /**
   * ⚠️ [C5c §5f] WHEN A COMMANDER WITHDREW THIS MARKING, AND OPTIONAL RATHER THAN NULLABLE.
   *
   * Present on a CORRECTION's answer — the marking it withdrew, when nothing replaced it — AND on a
   * replay (`duplicate: true`, through `POST /app/duty-events` or `/sync`) of a marking that has
   * since been withdrawn: a client re-sending a queued absence after its own correction gets the
   * withdrawn row back, stamped. [P6d]'s test: a constable who never corrects anything renders
   * nothing from it, so its very presence is the signal, and every other response is byte-for-byte
   * what it was.
   */
  voidedAt: z.iso.datetime().optional(),
});

/**
 * ⚠️ PARTIAL SUCCESS, REPORTED PER ITEM, AND THE TRANSPORT IS ALWAYS 200.
 *
 * A batch that HTTP-fails on one item leaves an offline client with two bad choices: drop
 * the good ones, or retry the bad one for ever. So the batch itself always succeeds and
 * each item carries its own verdict.
 *
 * ⚠️ KEYED ON `clientEventId` RATHER THAN ON POSITION, so a client reconciles without
 * depending on array order surviving the round trip.
 */
export const dutyEventSyncResultSchema = z.object({
  results: z.array(
    z.object({
      clientEventId: z.string(),
      ok: z.boolean(),
      event: dutyEventSchema.nullable(),
      error: z.object({ code: z.string(), message: z.string() }).nullable(),
    }),
  ),
});

export const assignmentDutyEventParamsSchema = z.object({ id: objectIdSchema });

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [C5c §5f] A COMMANDER CORRECTS THEIR OWN MARKING — `POST /app/duty-events/:id/correct`.
 *
 *  `:id` is the marking being corrected: the `eventId` of `attendanceMarkedBy`, which is always a
 *  TAPPED row (never a derived one). `to` is what the record should say instead:
 *
 *    • `PRESENT`  — the marking is withdrawn and a Mark Reached at the first stop is recorded in
 *                   its place, which derives the Start Duty exactly as a fresh one would.
 *    • `ABSENT`   — withdrawn, and an absence recorded with `reason`.
 *    • `UNMARKED` — withdrawn, and nothing recorded. The ladder then derives what it would have.
 *
 *  ⚠️ NO `clientEventId`. A correction is ONLINE-ONLY — it is not queued, so there is no
 *  capture-time id to replay against — and it is replay-safe by the marking it names instead: a
 *  second correction of an already-withdrawn marking changes nothing and answers with what the
 *  first one left. The replacement's own id is derived from the corrected one, `~c<id>`, in a
 *  namespace the wire charset forbids (`DERIVED_CLIENT_EVENT_ID_PREFIX`'s rule).
 *
 *  ⚠️ `geo` IS OPTIONAL, AND ITS ABSENCE IS RECORDED AS `UNAVAILABLE` RATHER THAN INVENTED. A
 *  replacement row carries the COMMANDER's position and clock (§5d), exactly as the marking it
 *  replaces did — but a correction made from a desk after the duty has none to give.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const CORRECTION_TARGETS = ['PRESENT', 'ABSENT', 'UNMARKED'] as const;
export type CorrectionTarget = (typeof CORRECTION_TARGETS)[number];

export const correctDutyEventBodySchema = z
  .object({
    to: z.enum(CORRECTION_TARGETS),
    /** ABSENT only, and required there — the same rule as a first marking. */
    reason: z.string().trim().min(1).max(300).optional(),
    /** ⚠️ THE COMMANDER'S DEVICE CLOCK, for the replacement row. Never reconciled. */
    capturedAt: z.iso.datetime(),
    geo: dutyEventGeoSchema.optional(),
  })
  .refine((body) => (body.to === 'ABSENT' ? body.reason !== undefined : body.reason === undefined), {
    path: ['reason'],
    message: REASON_BELONGS_TO_ABSENT_MESSAGE,
  });

export const dutyEventIdParamsSchema = z.object({ id: objectIdSchema });

/**
 * ⚠️ [C5c §5a] THREE FIGURES, ONE PER DISPLAY STATE — `presenceTallyOf`'s answer on the wire.
 *
 * Named `presence` wherever it is carried, never `attendance`: the web's fill slot already has an
 * `attendance` field holding the six-member ENUM, and one word for a state and a tally on one
 * screen is how a renderer reads the wrong one.
 */
export const presenceTallySchema = z.object({
  present: z.number().int().min(0),
  absent: z.number().int().min(0),
  notArrived: z.number().int().min(0),
}) satisfies z.ZodType<PresenceTally>;
